import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fadeUp } from "@/lib/motion";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated.");
      setTimeout(() => navigate("/admin/login"), 1800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not reset password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6">
      <Toaster position="top-center" />
      <motion.div
        {...fadeUp}
        data-testid="reset-page"
        className="w-full max-w-md bg-white border border-[#E5E5E5] p-10 md:p-14"
      >
        <Link to="/" className="font-serif text-2xl text-[#1A1F24]">
          Gemma <span className="italic text-[#C5A059]">Veda</span>
        </Link>
        <h1 className="font-serif text-3xl mt-6 text-[#1A1F24]">Choose a new password</h1>

        {!done ? (
          <>
            <p className="mt-2 text-sm text-[#5C6670]">Minimum 8 characters. Once set, you'll be sent to sign in.</p>
            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="reset-form">
              <div>
                <label className="gv-label">New password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="gv-input mt-2"
                  data-testid="reset-password"
                />
              </div>
              <div>
                <label className="gv-label">Confirm password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="gv-input mt-2"
                  data-testid="reset-confirm"
                />
              </div>
              <button type="submit" disabled={submitting} className="gv-btn-gold w-full justify-center" data-testid="reset-submit">
                {submitting ? "Updating…" : "Set new password"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="reset-done" className="mt-6">
            <div className="border-l-2 border-[#C5A059] pl-4 py-3 bg-[#FAFAFA]">
              <p className="text-sm text-[#1A1F24]">Password updated. Redirecting to sign-in…</p>
            </div>
          </div>
        )}

        <div className="mt-10">
          <Link to="/admin/login" className="text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]" data-testid="reset-back-login">
            ← Back to sign in
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
